import React, { useState, useRef, useEffect } from 'react';

interface CommentPromptProps {
  isOpen: boolean;
  onSubmit: (text: string) => Promise<void> | void;
  onClose: () => void;
  position?: { x: number; y: number };
  error?: string | null;
  submitting?: boolean;
}

export function CommentPrompt({
  isOpen,
  onSubmit,
  onClose,
  position,
  error,
  submitting = false,
}: CommentPromptProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setInput('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      await onSubmit(input.trim());
      setInput('');
    } catch {
      // Parent component renders the failure state.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-50 bg-white rounded-lg shadow-xl border p-3 w-80"
      style={position ? { left: position.x, top: position.y } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
    >
      <form onSubmit={handleSubmit}>
        <div className="text-sm font-medium text-gray-700 mb-2">
          Add comment
        </div>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          placeholder="Leave a comment..."
          className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
        {error && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!input.trim() || submitting}
            className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {submitting ? 'Commenting...' : 'Comment'}
          </button>
        </div>
      </form>
    </div>
  );
}
